import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { exitProjectDataMode } from '../../../store/dataFabricSlice';
import { clearProjectOrigin } from '../../../store/unstructuredThreadSlice';
import GetCookie from '../../utils/GetCookie';
import ThemeToggle from '../../globalComponents/ThemeToggle';
import Footer from '../../globalComponents/Footer';
import NotificationDropdown from '../../globalComponents/NotificationDropdown';
import AiSearchComponent, { SerachDisplayDiv } from '../../utils/AiSearch';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProfilePopup from '../../globalComponents/ProfilePopup';
import { ProfileCache, NavStateCache, CategoryCache } from '../../globalComponents/NavigationCache';// ✅ UPDATED IMPORT
import authNavigate from '../../globalComponents/AuthNav';
import store from '../../../store/store';
import { getBrowserTimezone, clearFormatterCache } from '../../utils/dateUtils';
import { useTimezone } from '../../utils/TimezoneContext';


const SIDEBAR_COLLAPSE_KEY = 'iam-console:sidebar-collapsed';

function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

export const TopNav = () => {
  const accessToken = GetCookie('access_token');
  const navigate = useNavigate();
  const user_at = 'iam-console';
  const [response_ai, set_response_ai] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { timezone, setTimezone } = useTimezone();
  // Profile states
  const [profileData, setProfileData] = useState(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const profileButtonRef = useRef(null);

  // Fetch profile on mount
  useEffect(() => {
    fetchProfileDetails();
  }, []);

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileButtonRef.current && !profileButtonRef.current.contains(event.target)) {
        setShowProfilePopup(false);
      }
    };
    if (showProfilePopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfilePopup]);

  const fetchProfileDetails = async () => {
    // Show whatever's cached right away (instant paint, no loading flash),
    // but always fall through to the live call below too — a role changed
    // elsewhere (Admin Console) must show up on the next mount/refresh, not
    // wait out the cache's 3h TTL or require a logout. profile_service always
    // queries iam_users fresh, so the network call is cheap and authoritative.
    const cachedProfile = ProfileCache.get(user_at);
    if (cachedProfile) {
      setProfileData(cachedProfile);
    } else {
      setIsProfileLoading(true);
    }
    try {
      const response = await axios.post(
        '/pceapi/general/profile-details',
        { console_type: user_at },
        { headers: { Authorization: accessToken, 'Content-Type': 'application/json' } }
      );
      const data = response.data;

      if (data.status === 'NotAuthorized') {
        navigate('/login');
        return;
      }

      if (data.status === 'failed') {
        if (!cachedProfile) setProfileError(data.message || 'Failed to load profile details');
        return;
      }

      if (data.status === 'success') {
      const fetchedTz = data.data.timezone;
        
        if (!fetchedTz) {
          const browserTz = getBrowserTimezone();
          data.data.timezone = browserTz;
          
          if (timezone !== browserTz) {
            clearFormatterCache();
            setTimezone(browserTz);
          }
          
          axios.post(
            '/pceapi/general/update-user-preferences',
            { timezone: browserTz, console_type: user_at },
            { headers: { Authorization: accessToken, 'Content-Type': 'application/json' } }
          ).catch(err => console.error('Auto-save timezone failed:', err));
        } else if (fetchedTz !== timezone) {
          clearFormatterCache();
          setTimezone(fetchedTz);
        }
        setProfileData(data.data);
        ProfileCache.set(user_at, data.data);
      }
    } catch (error) {
      console.error('Error fetching profile details:', error);
      if (!cachedProfile) {
        setProfileError('Unable to load profile. Please check your connection and try again.');
      }
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleProfileUpdate = (newImageUrl) => {
    const updatedProfile = {
      ...profileData,
      profile_image_url: newImageUrl
    };
    setProfileData(updatedProfile);
    ProfileCache.set(user_at, updatedProfile);
  };

  const handleLogout = async () => {
    try {
      const response = await axios.get('/pceapi/auth/iamuser/logout', {
        headers: { Authorization: accessToken }
      });
      if (response.data.status === 'success') {
        store.dispatch({ type: 'RESET_ALL_STATE' });
        ProfileCache.clear(user_at); // Clear profile cache
        NavStateCache.clear(user_at); // ✅ Clear nav state cache on logout
        sessionStorage.removeItem('hta_selected_category'); // Clear category selection on logout
        clearCookie('access_token');
        navigate('/login');
      } else {
        console.error('Logout failed:', response.data.message);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  return (
    <>
      <nav className="navbar navbar-expand-lg fixed-top topnavbar col-lg-10 col-md-9 col-sm-9">
        <div className="container-fluid ps-0">
          <div className="justify-content-center">
            <AiSearchComponent
              tok={accessToken}
              where_user={user_at}
              ai_response={response_ai}
              setResponse={set_response_ai}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              setError={setError}
            />
          </div>
          <div className="">
            {/* <span className="ev-message-card1 ev-btn-med ev-bold-reg ms-3">HTA Dossier</span> */}
            <span>&nbsp;&nbsp;&nbsp;</span>

            <ThemeToggle />

            <button className="ev-btn-blue-fill-round ev-btn-med ev-bold-reg ms-3" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right"></i> Logout
            </button>

            <div className="d-inline-block position-relative ms-3" ref={profileButtonRef}>
              {isProfileLoading ? (
                <button className="ev-btn-lightblue-fill-round nav-round-btn">
                  <i className="bi bi-person-fill"></i>
                </button>
              ) : profileData?.profile_image_url ? (
                <button
                  className="avatar-tooltip bg-transparent border-0"
                  data-tooltip={profileData?.name || 'Profile'}
                  onClick={() => setShowProfilePopup(!showProfilePopup)}
                >
                  <img
                    src={profileData.profile_image_url}
                    className="nav-profile-image"
                    onLoad={(e) => e.target.classList.add('loaded')}
                  />
                </button>
              ) : (
                <button
                  className="ev-btn-lightblue-fill-round nav-round-btn avatar-tooltip"
                  data-tooltip={profileData?.name || 'Profile'}
                  onClick={() => setShowProfilePopup(!showProfilePopup)}
                >
                  <i className="bi bi-person-fill"></i>
                </button>
              )}
              <ProfilePopup
                profileData={profileData}
                consoleType={user_at}
                onClose={() => setShowProfilePopup(false)}
                onProfileUpdate={handleProfileUpdate}
                isOpen={showProfilePopup}
              />
            </div>

            <Link
              to="/iam-user-guide"
              className="ev-btn-lightblue-fill-round nav-round-btn ms-3 avatar-tooltip text-decoration-none d-inline-flex align-items-center justify-content-center"
              data-tooltip="User Help Guide"
            >
              <i className="bi bi-patch-question-fill"></i>
            </Link>

            <NotificationDropdown user_type={"IAM"} />
          </div>
        </div>
      </nav>

      {(isLoading || response_ai !== '' && error === null) && (
        <SerachDisplayDiv
          ai_response={response_ai}
          set_ai_response={set_response_ai}
          error={error}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          setError={setError}
          clearQuestion={() => set_response_ai('')}
        />
      )}

      {error !== null && error !== '' && (
        <ErrorPopup
          onClose={() => setError(null)}
          message={error}
        />
      )}

      {profileError && (
        <ErrorPopup
          onClose={() => {
            setProfileError(null);
            fetchProfileDetails();
          }}
          message={profileError}
        />
      )}
    </>
  );
};

export const SideNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // True while the user is managing project data — keeps the HTA section lit
  // even on the Data Manager route, so they feel they never left the project.
  const inProjectDataMode = useSelector((state) => state.dataFabric.fromProject);
  const accessToken = GetCookie('access_token');
  const consoleType = 'iam-console';
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [selectedSideNavCategory, setSelectedSideNavCategory] = useState(null);
  // 1. Initial State
  const getInitialNavState = () => {
    const cachedState = NavStateCache.get(consoleType);
    if (cachedState) return cachedState;
    return { modules: false, tools: false, slr: false, hta: true };
  };

  const getInitialCollapsed = () => NavStateCache.get(SIDEBAR_COLLAPSE_KEY) === true;

  const [expanded, setExpanded] = useState(getInitialNavState());
  const [collapsed, setCollapsed] = useState(getInitialCollapsed());
  const [htaCategories, setHtaCategories] = useState([]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (collapsed) {
      root.setAttribute('data-sidebar', 'collapsed');
    } else {
      root.removeAttribute('data-sidebar');
    }
    return () => {
      root.removeAttribute('data-sidebar');
    };
  }, [collapsed]);

useEffect(() => {
  if (!location.pathname.includes("/hta-dossier") && !location.pathname.includes("/dashboard")) {
    setSelectedSideNavCategory(null);
    sessionStorage.removeItem('hta_selected_category');
  }
}, [location.pathname]);
useEffect(() => {
  const savedCategory = sessionStorage.getItem('hta_selected_category');
  if (savedCategory) {
    setSelectedSideNavCategory(savedCategory);
  }
}, []);
  useEffect(() => {
    fetchCategories();
    const handleCategoryUpdate = () => {
      CategoryCache.remove && CategoryCache.remove('hta_categories');
      fetchCategories(true);
    };

    window.addEventListener('category_updated', handleCategoryUpdate);

    return () => {
      window.removeEventListener('category_updated', handleCategoryUpdate);
    };
  }, []);

  const fetchCategories = async (force = false) => {
    if (!force) {
      const cachedCats = CategoryCache.get('hta_categories');
      if (cachedCats) {
        setHtaCategories(cachedCats);
        return;
      }
    }
    try {
      const response = await axios.post('/pceapi/fabrics/list',
        { page: 1, size: 1 },
        { headers: { Authorization: accessToken } }
      );
      if (response.data.status === 'success') {
        const cats = response.data.categories || ['General'];
        setHtaCategories(cats);
        CategoryCache.set('hta_categories', cats);
      }
    } catch (error) {
      console.error("SideNav Category Fetch Error:", error);
    }
  };

  const isActive = (path) => {
    // While managing project data, Data Manager must not steal the highlight
    if (inProjectDataMode && path === "/data-manager") return '';
    return location.pathname === path ? 'active' : '';
  };

  // Any sidebar navigation ends the project-data context, so a manual visit
  // to Data Manager always shows the plain experience. genbi_fabric_id is the
  // other standing flag that needs clearing here: GenbiDashboard now keeps it
  // in sessionStorage (rather than consuming it on first read) so a hard
  // refresh on a project-scoped /hta-dossier still shows that one project —
  // which means a deliberate sidebar click is the only thing left to end
  // that scoping and return to the unscoped view.
  const clearProjectDataMode = () => {
    dispatch(exitProjectDataMode());
    dispatch(clearProjectOrigin());
    sessionStorage.removeItem('genbi_fabric_id');
  };

  const handleClick = (event, path) => {
    event.preventDefault();
    // Already on this page — skip the auth check/navigation, same as the
    // HTA Dossier Studio link does for /dashboard. In project-data mode we
    // still fall through so the click exits it and shows the plain Data Manager.
    if (location.pathname === path && !inProjectDataMode) return;
    clearProjectDataMode();
    authNavigate(path, 'iam', navigate);
  };

  const handleCategoryClick = (event, categoryName) => {
    event.preventDefault();
    clearProjectDataMode();

    // 1. Set Local Active State
    setSelectedSideNavCategory(categoryName);

    // 2. Dispatch custom event for siblings (GenbiDashboard)
    const eventObj = new CustomEvent('hta_category_change', {
      detail: categoryName
    });
    window.dispatchEvent(eventObj);

    // 3. Save to sessionStorage for cross-route navigation
    sessionStorage.setItem('hta_selected_category', categoryName);

    // 4. Ensure we are on the right route
    if (!location.pathname.includes("/dashboard")) {
      authNavigate('/dashboard', 'iam', navigate);
    }
  };

  const handleHTAStudioClick = (event) => {
    event.preventDefault();
    clearProjectDataMode();

    // 1. Clear selected category highlight
    setSelectedSideNavCategory(null);

    // 2. Clear sessionStorage
    sessionStorage.removeItem('hta_selected_category');

    // 3. Dispatch event to clear category filter in DataFabricManagement
    window.dispatchEvent(new CustomEvent('hta_show_all'));

    // 4. Navigate to dashboard
    if (!location.pathname.includes("/dashboard")) {
      authNavigate('/dashboard', 'iam', navigate);
    }
  };

  const toggleExpand = (branch) => {
    const newExpandedState = { ...expanded, [branch]: !expanded[branch] };
    setExpanded(newExpandedState);
    NavStateCache.set(consoleType, newExpandedState);
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => {
      const next = !prev;
      NavStateCache.set(SIDEBAR_COLLAPSE_KEY, next);
      return next;
    });
  };

  const isHtaSectionActive =
    location.pathname.includes("/hta-dossier") ||
    location.pathname.includes("/dashboard") ||
    (inProjectDataMode && location.pathname.includes("/data-manager"));

  return (
    <nav className={`col-lg-2 col-md-3 col-sm-3 sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="position-sticky">

        {collapsed ? (
          /* --- Collapsed: mini logo that swaps to toggle icon on hover --- */
          <div className="sidebar-top sidebar-top-collapsed">
            <button
              type="button"
              className="sidebar-logo-toggle avatar-tooltip avatar-tooltip-bottom"
              onClick={toggleCollapse}
              data-tooltip="Open sidebar"
              aria-label="Open sidebar"
            >
              <img
                src="media\Bristol-Myers-Squibb-Collapsed.png"
                className="sidebar-logo-mini logo-swap-img"
                alt="Logo"
              />
              <i className="bi bi-layout-sidebar logo-swap-icon"></i>
            </button>
          </div>
        ) : (
          /* --- Expanded: full logo (home link) + toggle button on right --- */
          <div className="sidebar-top d-flex align-items-center justify-content-between">
            <Link to="/" className="text-decoration-none">
              <img
                src="/media/bms_logo.png"
                className="logo-color w-90"
                alt="Logo"
              />
            </Link>
            <button
              type="button"
              className="sidebar-toggle-btn avatar-tooltip avatar-tooltip-bottom"
              onClick={toggleCollapse}
              data-tooltip="Close sidebar"
              aria-label="Close sidebar"
            >
              <i className="bi bi-layout-sidebar"></i>
            </button>
          </div>
        )}

        {/* --- HTA Dossier Studio + Navigation --- */}
        <div className="nav-branch">
          <div className={`branch-content ${collapsed ? 'mt-5' : 'mt-5'}`}>

            {/* HTA Dossier Studio — single element, text clips via CSS when collapsed */}
            <Link
              className={`sidelink ${collapsed ? 'sidelink-icon avatar-tooltip' : ''} ${isHtaSectionActive ? 'active' : ''}`}
              data-tooltip={collapsed ? 'HTA Dossier Studio' : undefined}
              onClick={(e) => {
                handleHTAStudioClick(e);
                if (!expanded.hta) toggleExpand('hta');
              }}
            >
              <i className="bi bi-kanban"></i>
              <span className="sidelink-text">HTA Dossier Studio</span>
            </Link>

            {/* Categories List */}
            {/* {expanded.hta && (
              <div className="mt-1 mb-1">
                {htaCategories.map((cat, index) => {
                  const isCatActive = selectedSideNavCategory === cat;

                  return (
                    <Link
                      key={index}
                      className={`sidelink sidelink-sub my-0 ${isCatActive ? 'active' : ''}`}
                      onMouseEnter={() => setHoveredCategory(cat)}
                      onMouseLeave={() => setHoveredCategory(null)}
                      onClick={(e) => handleCategoryClick(e, cat)}
                    >
                      <i className="bi bi-bookmarks"></i>
                      <span>{cat}</span>
                    </Link>
                  );
                })}
              </div>
            )} */}
          </div>
        </div>

        {/* Data Manager - separate from HTA branch */}
        <div className="nav-branch mt-0">
          <div className="branch-content">
            {/* Data Manager */}
            <Link
              className={`sidelink ${collapsed ? 'sidelink-icon avatar-tooltip' : ''} ${isActive("/data-manager")}`}
              data-tooltip={collapsed ? 'Data Manager' : undefined}
              onClick={(e) => handleClick(e, "/data-manager")}
            >
              <i className="bi bi-bounding-box-circles"></i>
              <span className="sidelink-text">Data Manager</span>
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </nav>
  );
};